import { useState } from 'react';
import styled from 'styled-components';

import { HtmlEditor } from 'modules/graphql/types';
import { Tab, TabGroup } from 'ui/tabs';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';

import { useContentEditorPreferencesContext } from 'modules/templateEditor/context/ContentEditorPreferencesContext';
import { NoOpComponent } from 'utils/reactHelpers';
import { displayContent } from 'modules/templateEditor/common';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { TabCustomFonts } from './TabCustomFonts';
import { TabStyling } from './TabStyling';

export const CONTENT_EDITOR_PREFERENCES_MODAL_TEST_ID = 'content-editor-preferences-modal';

enum PreferenceTab {
  CustomFonts = 'customFonts',
  Styling = 'styling',
}

const TABS_CONTENT_COMPONENT_MAPPER = {
  [PreferenceTab.CustomFonts]: TabCustomFonts,
  [PreferenceTab.Styling]: TabStyling,
};

const StyledModalTabContainer = styled(ModalBody)`
  background-color: ${({ theme }) => theme.colors.backgroundColors.backgroundSecondary};
  border: none;
  display: flex;
  overflow: visible;
  padding: ${({ theme }) => `${theme.spacing.values.plusHalf} ${theme.spacing.values.plusHalf} 0`};
`;

export const ContentEditorPreferencesModal = () => {
  const { contentEditor } = useEditableFieldsContext();
  const showCustomFonts = contentEditor === HtmlEditor.Bee;
  const [selectedTab, setSelectedTab] = useState<PreferenceTab>(
    showCustomFonts ? PreferenceTab.CustomFonts : PreferenceTab.Styling,
  );

  const { toggleModal } = useContentEditorPreferencesContext();
  const CurrentTabContent = TABS_CONTENT_COMPONENT_MAPPER[selectedTab] ?? NoOpComponent;

  return (
    <Modal
      isModalOpen
      onClose={() => toggleModal(false)}
      id="ContentEditorPreferencesModal"
      data-test={CONTENT_EDITOR_PREFERENCES_MODAL_TEST_ID}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader autoFocus={false}>{displayContent.headerPreferences}</ModalHeader>

      <StyledModalTabContainer>
        <TabGroup
          selectedTab={selectedTab}
          onTabChange={setSelectedTab}
          data-test={`${CONTENT_EDITOR_PREFERENCES_MODAL_TEST_ID}-tabs`}
        >
          {showCustomFonts && (
            <Tab id={PreferenceTab.CustomFonts} label={displayContent.labelCustomFonts} />
          )}
          <Tab id={PreferenceTab.Styling} label={displayContent.labelStyling} />
        </TabGroup>
      </StyledModalTabContainer>

      <CurrentTabContent />

      <ModalFooter />
    </Modal>
  );
};
